// OnboardingOverlay — 19BI · first-use stepper
//
// Displays a 4-step overlay when no CourseDrafts exist in IDB (first visit).
// Dismisses permanently via localStorage flag or when user creates a course.
//
// Steps: Create course → Generate content → Study → Refine
// Keyboard: ArrowLeft/Right or 1-4 to navigate, Escape to skip, Enter to advance.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Sparkles, Target, RefreshCcw, X } from 'lucide-react';
import { useT } from '../i18n/index.js';

const STEPS = [
  { titleKey: 'onboard.step1Title', textKey: 'onboard.step1Text', Icon: BookOpen },
  { titleKey: 'onboard.step2Title', textKey: 'onboard.step2Text', Icon: Sparkles },
  { titleKey: 'onboard.step3Title', textKey: 'onboard.step3Text', Icon: Target },
  { titleKey: 'onboard.step4Title', textKey: 'onboard.step4Text', Icon: RefreshCcw },
] as const;

const DISMISS_KEY = 'examforge.onboard.dismissed';

export function shouldShowOnboard(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !localStorage.getItem(DISMISS_KEY);
  } catch {
    return true;
  }
}

export function dismissOnboard(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch { /* localStorage unavailable — session only */ }
}

interface OnboardingOverlayProps {
  onDismiss: () => void;
}

export function OnboardingOverlay({ onDismiss }: OnboardingOverlayProps) {
  const t = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;

  const handleDismiss = useCallback(() => {
    dismissOnboard();
    onDismiss();
  }, [onDismiss]);

  const handleNext = useCallback(() => {
    if (isLast) {
      handleDismiss();
    } else {
      setStep((s) => s + 1);
    }
  }, [isLast, handleDismiss]);

  const handleStart = useCallback(() => {
    handleDismiss();
    navigate('/create');
  }, [handleDismiss, navigate]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { handleDismiss(); return; }
      if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); handleNext(); return; }
      if (e.key === 'ArrowLeft') { setStep((s) => Math.max(0, s - 1)); return; }
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= STEPS.length) { setStep(num - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleDismiss, handleNext]);

  const { titleKey, textKey, Icon } = STEPS[step]!;

  return (
    <div className="onboard-backdrop" role="dialog" aria-modal="true" aria-label={t('onboard.welcome')}>
      <div className="onboard-card">
        <button
          type="button"
          className="onboard-close"
          onClick={handleDismiss}
          aria-label={t('onboard.skip')}
        >
          <X size={18} />
        </button>

        <h2 className="onboard-title">{t('onboard.welcome')}</h2>
        <p className="onboard-subtitle">{t('onboard.welcomeSub')}</p>

        <div className="onboard-step">
          <div className="onboard-step-icon">
            <Icon size={32} aria-hidden="true" />
          </div>
          <h3 className="onboard-step-title">{t(titleKey)}</h3>
          <p className="onboard-step-text">{t(textKey)}</p>
        </div>

        {/* Progress dots */}
        <div className="onboard-dots">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`onboard-dot${i === step ? ' is-active' : ''}`}
              aria-hidden="true"
            />
          ))}
        </div>

        <div className="onboard-actions">
          <button type="button" className="onboard-skip" onClick={handleDismiss}>
            {t('onboard.skip')}
          </button>
          <button type="button" className="onboard-next" onClick={handleNext}>
            {isLast ? t('onboard.done') : t('onboard.next')}
          </button>
        </div>

        {isLast && (
          <button type="button" className="onboard-start" onClick={handleStart}>
            {t('onboard.start')} →
          </button>
        )}
      </div>
    </div>
  );
}
